import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import SaveIcon from '@mui/icons-material/Save';
import { addDoc, collection, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { auth, db, functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import {
  AI_AGENT_TOOL_OPTIONS,
  DEFAULT_CONTEXT_MEMORY_LIMIT,
  MAX_CONTEXT_MEMORY_LIMIT,
  MIN_CONTEXT_MEMORY_LIMIT,
  normalizeAllowedTools,
  normalizeContextMemoryLimit,
} from '../../lib/aiAgentCapabilities';
import { AI_AGENT_ROLE_PRESETS } from '../../lib/aiAgentRoles';
import {
  AI_ROSTER_AGENT_STATUSES,
  AI_ROSTER_MODEL_OPTIONS,
  DEFAULT_MODEL_TARGET,
  normalizeAgentStatus,
  normalizeModelTarget,
  uiStatusToStudioStatus,
} from '../../lib/aiWorkforceRoster';
import {
  evaluateModelTargetSubmit,
  fetchAiProviderAvailability,
  isModelTargetAvailable,
  modelTargetAvailabilityTooltip,
} from '../../lib/aiProviderAvailability';
import {
  DEFAULT_NON_DESTRUCTIVE_PROTOCOL,
  ensureNonDestructiveProtocol,
  ensureSystemIntegrityProtocol,
  ensureCriticalPatchingStandards,
  ensureRejectionHandlingProtocol,
  stripSystemIntegrityProtocol,
} from '../../constants/aiProtocols';
import { buildFrictionBootPrefill } from '../../lib/frictionSuggestions';

const TEMP_MARKS = [
  { value: 0, label: '0' },
  { value: 0.5, label: '0.5' },
  { value: 1, label: '1' },
];

const markFrictionSuggestionApplied = httpsCallable(
  functions,
  'markFrictionSuggestionApplied',
);

/**
 * @param {object | null | undefined} agent
 */
function buildFormStateFromAgent(agent) {
  if (!agent) {
    return {
      name: '',
      roleTitle: '',
      temperature: 0.7,
      systemPrompt: DEFAULT_NON_DESTRUCTIVE_PROTOCOL,
      defaultBootContext: '',
      allowedTools: [],
      contextMemoryLimit: DEFAULT_CONTEXT_MEMORY_LIMIT,
      modelTarget: DEFAULT_MODEL_TARGET,
      status: 'enabled',
    };
  }
  return {
    name: agent.name || '',
    roleTitle: agent.roleTitle || '',
    temperature: Number.isFinite(Number(agent.temperature)) ? Number(agent.temperature) : 0.7,
    systemPrompt: stripSystemIntegrityProtocol(agent.systemPrompt || ''),
    defaultBootContext:
      agent.default_boot_context || agent.defaultBootContext || '',
    allowedTools: normalizeAllowedTools(agent.allowedTools),
    contextMemoryLimit: normalizeContextMemoryLimit(
      agent.contextMemoryLimit ?? DEFAULT_CONTEXT_MEMORY_LIMIT,
    ),
    modelTarget: normalizeModelTarget(agent.modelTarget),
    status: normalizeAgentStatus(agent.status),
  };
}

/**
 * Hire or edit an AI Studio agent.
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   companyId: string,
 *   agent?: object | null,
 *   onSaved?: () => void,
 *   frictionSuggestionId?: string | null,
 *   frictionDraftText?: string | null,
 * }} props
 */
export default function AgentDialog({
  open,
  onClose,
  companyId,
  agent = null,
  onSaved,
  frictionSuggestionId = null,
  frictionDraftText = null,
}) {
  const theme = useTheme();
  const isEdit = Boolean(agent?.id);
  const [name, setName] = useState('');
  const [roleTitle, setRoleTitle] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [systemPrompt, setSystemPrompt] = useState(DEFAULT_NON_DESTRUCTIVE_PROTOCOL);
  const [defaultBootContext, setDefaultBootContext] = useState('');
  const [allowedTools, setAllowedTools] = useState([]);
  const [contextMemoryLimit, setContextMemoryLimit] = useState(DEFAULT_CONTEXT_MEMORY_LIMIT);
  const [modelTarget, setModelTarget] = useState(DEFAULT_MODEL_TARGET);
  /** Normalized model at dialog open (edit only); used by submit leave-as-is guard. */
  const [baselineModelTarget, setBaselineModelTarget] = useState(null);
  const [status, setStatus] = useState('enabled');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [providerAvailability, setProviderAvailability] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [promptBeforeForFriction, setPromptBeforeForFriction] = useState('');

  useEffect(() => {
    if (!open) return;
    const next = buildFormStateFromAgent(agent);
    setName(next.name);
    setRoleTitle(next.roleTitle);
    setTemperature(next.temperature);
    setSystemPrompt(next.systemPrompt);
    const boot = frictionDraftText
      ? buildFrictionBootPrefill(frictionDraftText, next.defaultBootContext)
      : next.defaultBootContext;
    setDefaultBootContext(boot);
    setPromptBeforeForFriction(next.defaultBootContext || '');
    setAllowedTools(next.allowedTools);
    setContextMemoryLimit(next.contextMemoryLimit);
    setModelTarget(next.modelTarget);
    setBaselineModelTarget(isEdit ? next.modelTarget : null);
    setStatus(next.status);
    setError('');
  }, [open, agent, isEdit, frictionDraftText]);

  useEffect(() => {
    if (!open || !companyId) return undefined;
    let cancelled = false;
    setAvailabilityLoading(true);
    fetchAiProviderAvailability(companyId)
      .then((data) => {
        if (!cancelled) setProviderAvailability(data);
      })
      .catch(() => {
        if (!cancelled) {
          // Fail closed: treat both providers as unavailable.
          setProviderAvailability({
            companyId,
            requireTenantApiKeys: true,
            providers: {
              google: { status: 'unavailable', available: false },
              anthropic: { status: 'unavailable', available: false },
              local: { status: 'unavailable', available: false },
              deepseek: { status: 'unavailable', available: false },
            },
          });
        }
      })
      .finally(() => {
        if (!cancelled) setAvailabilityLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, companyId]);

  const resetForm = () => {
    const next = buildFormStateFromAgent(null);
    setName(next.name);
    setRoleTitle(next.roleTitle);
    setTemperature(next.temperature);
    setSystemPrompt(next.systemPrompt);
    setDefaultBootContext(next.defaultBootContext);
    setAllowedTools(next.allowedTools);
    setContextMemoryLimit(next.contextMemoryLimit);
    setModelTarget(next.modelTarget);
    setBaselineModelTarget(null);
    setStatus(next.status);
    setError('');
  };

  const toggleTool = (toolId) => {
    setAllowedTools((prev) =>
      prev.includes(toolId) ? prev.filter((t) => t !== toolId) : [...prev, toolId],
    );
  };

  const handleClose = () => {
    if (saving) return;
    resetForm();
    onClose();
  };

  const handleMemoryChange = (e) => {
    const raw = e.target.value;
    if (raw === '') {
      setContextMemoryLimit('');
      return;
    }
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) return;
    setContextMemoryLimit(Math.min(MAX_CONTEXT_MEMORY_LIMIT, Math.max(MIN_CONTEXT_MEMORY_LIMIT, n)));
  };

  const handleSubmit = async () => {
    if (!companyId) return;
    if (!name.trim()) {
      setError('Agent name is required.');
      return;
    }
    if (!roleTitle.trim()) {
      setError('Role / title is required.');
      return;
    }
    if (!systemPrompt.trim()) {
      setError('System prompt is required.');
      return;
    }
    if (allowedTools.length === 0) {
      setError('Select at least one allowed capability.');
      return;
    }

    const modelGuard = evaluateModelTargetSubmit({
      isEdit,
      baselineModelTarget,
      modelTarget,
      availability: providerAvailability,
      normalizeModelTarget,
    });
    if (!modelGuard.ok) {
      setError(modelGuard.error);
      return;
    }

    const memory =
      contextMemoryLimit === ''
        ? DEFAULT_CONTEXT_MEMORY_LIMIT
        : Math.min(
            MAX_CONTEXT_MEMORY_LIMIT,
            Math.max(MIN_CONTEXT_MEMORY_LIMIT, Number(contextMemoryLimit)),
          );

    setSaving(true);
    setError('');
    try {
      const actor = auth.currentUser;
      const basePrompt = isEdit
        ? systemPrompt.trim()
        : ensureNonDestructiveProtocol(systemPrompt.trim(), roleTitle.trim());
      let resolvedPrompt = ensureSystemIntegrityProtocol(basePrompt);
      resolvedPrompt = ensureCriticalPatchingStandards(resolvedPrompt, {
        codeRolesOnly: true,
        roleTitle: roleTitle.trim(),
      });
      resolvedPrompt = ensureRejectionHandlingProtocol(resolvedPrompt, {
        codeRolesOnly: true,
        roleTitle: roleTitle.trim(),
      });
      const studioStatus = uiStatusToStudioStatus(status);
      const payload = {
        name: name.trim(),
        roleTitle: roleTitle.trim(),
        temperature: Number(temperature.toFixed(2)),
        systemPrompt: resolvedPrompt,
        default_boot_context: defaultBootContext.trim(),
        allowedTools: [...allowedTools],
        contextMemoryLimit: memory,
        modelTarget: modelGuard.resolvedModel,
        status: studioStatus,
        updatedAt: serverTimestamp(),
        updatedBy: actor?.uid || null,
        updatedByEmail: actor?.email || '',
      };

      if (isEdit && agent?.id) {
        await updateDoc(doc(db, 'ai_agents', agent.id), payload);
      } else {
        await addDoc(collection(db, 'ai_agents'), {
          companyId,
          ...payload,
          // Hire defaults to active when UI status is enabled (default).
          status: studioStatus || 'active',
          createdAt: serverTimestamp(),
          createdBy: actor?.uid || null,
          createdByEmail: actor?.email || '',
        });
      }

      if (frictionSuggestionId) {
        try {
          await markFrictionSuggestionApplied({
            suggestionId: frictionSuggestionId,
            agentId: agent?.id || null,
            promptBefore: promptBeforeForFriction,
            promptAfter: defaultBootContext.trim(),
          });
        } catch (markErr) {
          console.warn('AgentDialog markFrictionSuggestionApplied:', markErr);
        }
      }

      resetForm();
      onSaved?.();
      onClose();
    } catch (e) {
      console.error('AgentDialog:', e);
      setError(
        e.message ||
          (isEdit ? 'Could not save agent changes.' : 'Could not hire agent.'),
      );
    } finally {
      setSaving(false);
    }
  };

  const capabilityPanelSx = {
    p: 2,
    borderRadius: 1,
    border: 1,
    borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.25) : 'divider',
    bgcolor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.background.default, 0.55)
        : alpha(theme.palette.primary.main, 0.03),
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle sx={{ fontWeight: 700 }}>
        {isEdit ? 'Edit AI agent' : 'Hire new AI agent'}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {isEdit
              ? 'Update this agent’s behavior, capabilities, and memory limits.'
              : 'Define an autonomous agent profile — behavior, operational boundaries, and memory limits keep agents modular inside your workspace.'}
          </Typography>

          {error && <Alert severity="error">{error}</Alert>}

          {frictionSuggestionId && (
            <Alert severity="warning">
              Friction draft prefilled in boot context only. Verify cited tickets
              before Save — Approve never auto-applied this text.
            </Alert>
          )}

          <TextField
            label="Agent name"
            placeholder="e.g. Nova"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            disabled={saving}
          />

          <Autocomplete
            freeSolo
            fullWidth
            options={AI_AGENT_ROLE_PRESETS}
            value={roleTitle}
            onChange={(_, newValue) => {
              setRoleTitle(typeof newValue === 'string' ? newValue : '');
            }}
            inputValue={roleTitle}
            onInputChange={(_, newInputValue) => {
              setRoleTitle(newInputValue);
            }}
            disabled={saving}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Worker Role / Title"
                placeholder="e.g. Tech Lead"
                required
                helperText="Select a preset role or type a custom title."
              />
            )}
          />

          <FormControl fullWidth disabled={saving || availabilityLoading}>
            <InputLabel id="agent-dialog-model-label">Model target</InputLabel>
            <Select
              labelId="agent-dialog-model-label"
              id="agent-dialog-model"
              label="Model target"
              value={modelTarget}
              onChange={(e) => setModelTarget(e.target.value)}
            >
              {AI_ROSTER_MODEL_OPTIONS.map((opt) => {
                const available = isModelTargetAvailable(
                  providerAvailability,
                  opt.id,
                );
                const tip = modelTargetAvailabilityTooltip(
                  providerAvailability,
                  opt.id,
                );
                return (
                  <MenuItem
                    key={opt.id}
                    value={opt.id}
                    disabled={!available}
                    title={tip || undefined}
                  >
                    {opt.label}
                  </MenuItem>
                );
              })}
            </Select>
            {modelTargetAvailabilityTooltip(
              providerAvailability,
              modelTarget,
            ) ? (
              <FormHelperText>
                {modelTargetAvailabilityTooltip(
                  providerAvailability,
                  modelTarget,
                )}
              </FormHelperText>
            ) : null}
          </FormControl>

          <FormControl fullWidth disabled={saving}>
            <InputLabel id="agent-dialog-status-label">Status</InputLabel>
            <Select
              labelId="agent-dialog-status-label"
              id="agent-dialog-status"
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {AI_ROSTER_AGENT_STATUSES.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Temperature — {temperature.toFixed(2)}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Lower values are more focused and deterministic; higher values are more creative.
            </Typography>
            <Slider
              value={temperature}
              onChange={(_, v) => setTemperature(v)}
              min={0}
              max={1}
              step={0.05}
              marks={TEMP_MARKS}
              disabled={saving}
              valueLabelDisplay="auto"
              sx={{ maxWidth: 480 }}
            />
          </Box>

          <Box sx={capabilityPanelSx}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
              Allowed Capabilities
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Tools this agent may invoke during autonomous runs. Unchecked capabilities are
              blocked at the platform boundary.
            </Typography>
            <FormGroup>
              {AI_AGENT_TOOL_OPTIONS.map((tool) => (
                <FormControlLabel
                  key={tool.id}
                  control={(
                    <Checkbox
                      size="small"
                      checked={allowedTools.includes(tool.id)}
                      onChange={() => toggleTool(tool.id)}
                      disabled={saving}
                    />
                  )}
                  label={(
                    <Box sx={{ py: 0.25 }}>
                      <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                        {tool.label}
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.disabled"
                          sx={{ ml: 1, fontWeight: 400 }}
                        >
                          ({tool.id})
                        </Typography>
                      </Typography>
                      {tool.description ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ lineHeight: 1.45, mt: 0.25 }}
                        >
                          {tool.description}
                        </Typography>
                      ) : null}
                    </Box>
                  )}
                  sx={{ alignItems: 'flex-start', ml: 0, mb: 1 }}
                />
              ))}
            </FormGroup>
          </Box>

          <TextField
            label="Max Thread Memory Context"
            type="number"
            value={contextMemoryLimit}
            onChange={handleMemoryChange}
            fullWidth
            required
            disabled={saving}
            slotProps={{
              htmlInput: {
                min: MIN_CONTEXT_MEMORY_LIMIT,
                max: MAX_CONTEXT_MEMORY_LIMIT,
                step: 1,
              },
            }}
            helperText="The number of recent thread messages the agent is allowed to analyze to keep token consumption and focus optimized."
          />

          <TextField
            label="Default boot context"
            placeholder="You have been assigned a new feature request. First, read the user's requirements below…"
            value={defaultBootContext}
            onChange={(e) => setDefaultBootContext(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            disabled={saving}
            helperText="Prepended on the agent's first ticket turn as [SYSTEM BOOT PROTOCOL] before [USER REQUEST]. Leave empty to skip."
          />

          <TextField
            label="System prompt"
            placeholder="You are a senior backend QA architect. Your job is to…"
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            fullWidth
            required
            multiline
            minRows={8}
            disabled={saving}
            helperText="Behavioral instructions this agent follows on every run. New agents include the Non-Destructive Config Protocol by default."
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={isEdit ? <SaveIcon /> : <PersonAddAlt1Icon />}
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? (isEdit ? 'Saving…' : 'Hiring…') : (isEdit ? 'Save changes' : 'Hire agent')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
